import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/components/ui/collapsible';
import { Button } from '@/shared/components/ui/button';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG } from '@/infrastructure/config/api';

interface AvailableApisListProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const apiGroups = [
  {
    group: 'Agent Base',
    endpoints: [
      { path: '/api/agent-base', method: 'GET, POST, DELETE', desc: 'List, install and uninstall workflows for a workspace' },
      { path: '/api/agent-base/{id}/executions', method: 'GET', desc: 'List executions for a specific workflow' },
      { path: '/api/agent-base/triggers', method: 'GET, POST', desc: 'List and execute manual triggers' },
    ],
  },
  {
    group: 'Workflow Executor',
    endpoints: [
      { path: '/api/workflow/execute', method: 'POST', desc: 'Execute a workflow test' },
      { path: '/api/workflow/{executionId}/status', method: 'GET', desc: 'Get execution status' },
      { path: '/api/workflow/{executionId}/events', method: 'GET', desc: 'Stream execution events (SSE)' },
      { path: '/api/workflow/{executionId}/stop', method: 'POST', desc: 'Stop a running execution' },
      { path: '/api/workflow/{executionId}/pause', method: 'POST', desc: 'Pause a running execution' },
      { path: '/api/workflow/{executionId}/continue', method: 'POST', desc: 'Resume a paused execution' },
    ],
  },
  {
    group: 'Workflow Base',
    endpoints: [
      { path: '/api/workflow-base', method: 'GET, POST, PUT, DELETE', desc: 'Manage workflow base definitions' },
    ],
  },
  {
    group: 'Nodes Executor',
    endpoints: [
      { path: '/api/nodes/execute', method: 'POST', desc: 'Execute a single node' },
      { path: '/api/nodes/status/{nodeId}', method: 'GET', desc: 'Get node execution status' },
    ],
  },
  {
    group: 'Integrations Base',
    endpoints: [
      { path: '/api/integrations-base', method: 'GET, POST, PUT, DELETE', desc: 'Manage integration configurations' },
      { path: '/api/external/integrations', method: 'GET', desc: 'List active integrations for the organization' },
    ],
  },
  {
    group: 'Knowledge Base',
    endpoints: [
      { path: '/api/knowledge-base/{workspaceId}', method: 'GET', desc: 'List knowledge bases for a workspace' },
      { path: '/api/knowledge-base/embeddings', method: 'GET, POST, PUT, DELETE', desc: 'Manage embeddings and semantic search' },
      { path: '/api/knowledge-base/storage', method: 'GET, POST, PUT, DELETE', desc: 'Upload, download and manage files' },
    ],
  },
  {
    group: 'Assistant',
    endpoints: [
      { path: '/api/assistant/chat', method: 'POST', desc: 'Send a message and receive AI response (streaming)' },
      { path: '/api/assistant/chat/history', method: 'GET', desc: 'Retrieve chat session history' },
    ],
  },
  {
    group: 'API Tokens',
    endpoints: [
      { path: '/api/api-token/auth/validate', method: 'GET', desc: 'Validate the current API token' },
    ],
  },
];

export function AvailableApisList({ isOpen, onOpenChange }: AvailableApisListProps) {
  const { t } = useTranslation();

  const totalEndpoints = apiGroups.reduce((sum, g) => sum + g.endpoints.length, 0);
  const swaggerUrl = API_CONFIG.API_URL.replace(/\/api\/?$/, '/swagger');

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <Button variant="outline" className="w-full justify-between">
          <span className="font-semibold">{t('settings.workspaces.apiTokens.availableApis')} ({totalEndpoints})</span>
          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4">
        <p className="text-sm text-muted-foreground mb-4">{t('settings.workspaces.apiTokens.apisDescription')}</p>
        <div className="space-y-4">
          {apiGroups.map((group) => (
            <div key={group.group}>
              <div className="text-sm font-semibold mb-1">{group.group}</div>
              <div className="space-y-1">
                {group.endpoints.map((ep) => (
                  <div key={ep.path} className="flex items-center justify-between px-3 py-1.5 rounded-md border gap-4">
                    <div className="min-w-0">
                      <div className="text-xs font-mono text-muted-foreground">{ep.path}</div>
                      <div className="text-xs text-muted-foreground/70">{ep.desc}</div>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">{ep.method}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <a
          href={swaggerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mt-2"
        >
          {t('settings.workspaces.apiTokens.swaggerLink', 'For full endpoint details, see the Swagger documentation')}
          <ExternalLink className="h-3 w-3" />
        </a>
      </CollapsibleContent>
    </Collapsible>
  );
}
